import type { Alarm } from '@ctcells/contract'
import { alarmDetailPath, PATHS } from '@ctcells/core'
import { severityLabel, t } from '@ctcells/i18n'
import { useNavigate } from 'react-router'

import { Button } from '../atoms/Button.tsx'
import { Modal } from '../atoms/Modal.tsx'
import { StatusBadge } from '../atoms/StatusBadge.tsx'
import { alarmSeverityTone, severityBadgeFlat } from '../atoms/statusTone.ts'

import { useAlarmPopup } from './useAlarmPopup.ts'

import './alarmPopup.css'

/**
 * 알람 팝업 창 — 셸(`ConsoleLayout`)이 본문 칸(`.shell__body`) 안에 하나 둔다. 자료와 규칙은 `useAlarmPopup.ts` 머리말.
 *
 * 🔴 **덮개는 본문 칸만**(`scope="body"`) — 머리줄 · 상시 작업 줄의 E-STOP 은 창이 떠 있어도 눌린다(07 §6).
 * 🔴 **[닫기]는 창만 닫는다** — 확인 · 해제가 아니다(DP-02). 줄을 누르면 그 알람 상세(`/alarms/:id`)로 간다.
 * 🔴 여러 건이면 한 창의 목록이다 — 창을 겹겹이 쌓지 않는다.
 */
export function AlarmPopup() {
  const { alarms, dismiss } = useAlarmPopup()
  const navigate = useNavigate()
  if (alarms.length === 0) return null

  const go = (path: string) => {
    dismiss()
    void navigate(path)
  }

  return (
    <Modal
      footer={
        <>
          <Button onClick={dismiss} size="pill">
            {t('alarmPopup.close')}
          </Button>
          <Button onClick={() => go(PATHS.alarms)} size="pill" variant="primary">
            {t('alarmPopup.open')}
          </Button>
        </>
      }
      description={t('alarmPopup.note')}
      onClose={dismiss}
      scope="body"
      size="sm"
      title={t('alarmPopup.title', { count: alarms.length })}
    >
      <ul className="alpop" aria-label={t('alarmPopup.title', { count: alarms.length })}>
        {alarms.map((alarm) => (
          <li key={alarm.alarm_id}>
            <button
              className="alpop__row"
              onClick={() => go(alarmDetailPath(alarm.alarm_id))}
              type="button"
            >
              <AlarmLine alarm={alarm} />
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  )
}

function AlarmLine({ alarm }: { alarm: Alarm }) {
  const severity = alarm.severity ?? 'INFO'
  return (
    <>
      <span className="alpop__head">
        <StatusBadge
          /* 바탕 있는 칩만 테를 걷는다 — 정보(톤 없음)는 테를 둔다(eric 2026-10-30 · `severityBadgeFlat`) */
          flat={severityBadgeFlat(severity, alarmSeverityTone(severity))}
          label={severityLabel(severity)}
          status={severity}
          tone={alarmSeverityTone(severity)}
        />
        {/* 🔴 코드 원문 노출은 eric 2026-09-21 「일단 그대로」 */}
        <span className="alpop__code">{alarm.error_code}</span>
        {alarm.module_id == null ? null : <span className="muted">{alarm.module_id}</span>}
      </span>
      {alarm.message == null ? null : <span className="alpop__msg">{alarm.message}</span>}
    </>
  )
}
